import React, { useState } from 'react';
import { Grid, Typography, Button, Paper, Fade, Box, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import HomeCareForm from './HomeCareServices';
import BusinessForm from './BusinessServ';
import { Helmet } from 'react-helmet';
import image1 from "../images/image1.jpeg";
import '../static/App.css';
import { Phone, Email, Schedule, LocationOn, SupportAgent, BusinessCenter } from '@mui/icons-material';

const Contact = () => {
    const [selectedOption, setSelectedOption] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const handleOptionClick = (option) => {
        setSelectedOption(option === selectedOption ? '' : option);
    };

    const contactMethods = [
        {
            icon: <Phone sx={{ fontSize: 30, color: '#1b8fc9' }} />,
            title: 'Phone',
            details: '(847)-483-8999',
            link: 'tel:8474838999',
            description: 'Call us directly during business hours'
        },
        {
            icon: <Email sx={{ fontSize: 30, color: '#1b8fc9' }} />,
            title: 'Email',
            details: 'Info@247assurecare.com',
            link: 'mailto:Info@247assurecare.com',
            description: 'Send us a message anytime'
        },
        {
            icon: <Schedule sx={{ fontSize: 30, color: '#1b8fc9' }} />,
            title: 'Business Hours',
            details: 'Monday - Friday',
            time: '9:00 AM - 5:00 PM',
            description: 'We\'re here to help you'
        },
        {
            icon: <LocationOn sx={{ fontSize: 30, color: '#1b8fc9' }} />,
            title: 'Service Areas',
            details: 'Cook, DuPage, Lake Counties',
            description: 'Serving Northern Illinois'
        }
    ];

    return (
        <div className='page-back'>
            <div className="container mt-3">
                <Helmet>
                    <title>24/7 AssureCare - Contact Us</title>
                    <meta name="description" content="Get in touch with 24/7 AssureCare for home care services, business inquiries, or support. We're here to help you and your loved ones."/>
                    <meta name="keywords" content="contact 24/7 AssureCare, home care contact, caregiver support, business inquiries, care consultation" />
                </Helmet>

                {/* Header Section */}
                <Box sx={{ textAlign:'center', mb:4 }}>
                    <Typography variant="h2" component="h1" sx={{ color:'white', fontWeight:'bold', mb:2, fontSize:isMobile?'2rem':'2.5rem' }}>CONTACT US</Typography>
                    <Typography variant="h6" sx={{ color:'white', maxWidth:'850px', margin:'0 auto' }}>
                    We're here to answer your questions and provide the care and support you need
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* LEFT SIDE */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ mb:3, borderRadius:3, boxShadow:3, overflow:'hidden' }}>
                            <Box sx={{ width:'100%', height:300 }}>
                            <iframe title="HQ Location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d759249.9589968167!2d-87.64178804999999!3d41.982670999999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4c9feb43490ccb8f%3A0xa1d167dc3b6037ef!2s24%2F7%20Assure%20Care%2C%20LLC!5e0!3m2!1sen!2sus!4v1756137707707!5m2!1sen!2sus" width="100%" height="100%" style={{ border:0 }} allowFullScreen="" loading="lazy"/>
                            </Box>
                            <CardContent sx={{ textAlign:'center' }}><Typography variant="body2" sx={{ color:'text.secondary' }}>Serving Cook, DuPage, and Lake Counties</Typography></CardContent>
                        </Card>

                        <Card sx={{ mb:3, borderRadius:3, boxShadow:3 }}>
                            <CardContent sx={{ p:3 }}>
                            <Typography variant="h6" sx={{ mb:3, color:'#1b8fc9', textAlign:'center', fontWeight:'bold' }}>How to Reach Us</Typography>
                            <Grid container spacing={2}>
                                {contactMethods.map((method,index)=>(
                                <Grid item xs={12} key={index}>
                                    <Box sx={{ display:'flex', alignItems:'flex-start', gap:2 }}>
                                    <Box sx={{ mt:0.5 }}>{method.icon}</Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight:'bold', color:'#1b8fc9' }}>{method.title}</Typography>
                                        {method.link ? <Typography variant="body2"><a href={method.link} style={{ textDecoration:'none', color:'#1b8fc9', fontWeight:'bold' }}>{method.details}</a></Typography> : <Typography variant="body2" sx={{ fontWeight:'bold' }}>{method.details}</Typography>}
                                        {method.time && <Typography variant="body2" sx={{ fontWeight:'bold' }}>{method.time}</Typography>}
                                        <Typography variant="caption" sx={{ color:'text.secondary', display:'block', mt:0.5 }}>{method.description}</Typography>
                                    </Box>
                                    </Box>
                                </Grid>
                                ))}
                            </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* RIGHT SIDE */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ mb:3, borderRadius:3, boxShadow:3 }}>
                            <CardContent sx={{ p:3, textAlign:'center' }}>
                            <SupportAgent sx={{ fontSize:40, color:'#1b8fc9', mb:1 }} />
                            <Typography variant="h6" sx={{ mb:2, color:'#1b8fc9', fontWeight:'bold' }}>What can we help you with?</Typography>
                            <Box sx={{ display:'flex', flexDirection:isMobile?'column':'row', gap:2, justifyContent:'center' }}>
                                <Button variant={selectedOption==='homecare'?'contained':'outlined'} onClick={()=>handleOptionClick('homecare')} startIcon={<SupportAgent />} sx={{ textTransform:'none', fontWeight:600, borderRadius:'10px', px:3, py:1, backgroundColor:selectedOption==='homecare'?'#1b8fc9':'transparent', color:selectedOption==='homecare'?'white':'#1b8fc9', borderColor:'#1b8fc9', '&:hover':{ backgroundColor:'#1b8fc9', color:'white', boxShadow:'0 0 8px 2px rgba(21,149,160,0.3)' } }}>Care Services</Button>
                                <Button variant={selectedOption==='business'?'contained':'outlined'} onClick={()=>handleOptionClick('business')} startIcon={<BusinessCenter />} sx={{ textTransform:'none', fontWeight:600, borderRadius:'10px', px:3, py:1, backgroundColor:selectedOption==='business'?'#1b8fc9':'transparent', color:selectedOption==='business'?'white':'#1b8fc9', borderColor:'#1b8fc9', '&:hover':{ backgroundColor:'#1b8fc9', color:'white', boxShadow:'0 0 8px 2px rgba(21,149,160,0.3)' } }}>Business Support</Button>
                            </Box>
                            </CardContent>
                        </Card>

                        <Box sx={{ width:'100%', minHeight:'647px', position:'relative', borderRadius:3, overflow:'hidden', boxShadow:3 }}>
                            <Fade in={selectedOption==='' } timeout={500}>
                            <Box sx={{ width:'100%', height:'100%', backgroundImage:`url(${image1})`, backgroundSize:'cover', backgroundPosition:'center', position:'absolute', top:0, left:0, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'white', textAlign:'center', p:4, backgroundColor:'rgba(0,0,0,0.7)', backgroundBlendMode:'overlay' }}>
                                <Typography variant="h4" component="h2" sx={{ mb:2, fontWeight:'bold', textShadow:'2px 2px 4px rgba(0,0,0,0.5)' }}>How Can We Help You Today?</Typography>
                                <Typography variant="h6" sx={{ mb:3, textShadow:'1px 1px 3px rgba(0,0,0,0.5)' }}>Select an inquiry type to get started with your personalized consultation</Typography>
                                <SupportAgent sx={{ fontSize:60, mb:2 }} />
                            </Box>
                            </Fade>

                            <Fade in={selectedOption==='homecare'} timeout={500}><Box sx={{ position:'absolute', top:0, left:0, width:'100%', bgcolor:'background.paper' }}><HomeCareForm /></Box></Fade>
                            <Fade in={selectedOption==='business'} timeout={500}><Box sx={{ position:'absolute', top:0, left:0, width:'100%', bgcolor:'background.paper' }}><BusinessForm /></Box></Fade>
                        </Box>
                    </Grid>
                </Grid>
            </div>
        </div>
        );

};

export default Contact;